import { bytesToHex, hexToBytes, randomBytes } from '@noble/hashes/utils.js'

import { Channel, connectionKeys, sessionId } from './box'
import {
    type ClearFrame,
    type CompanionMessage,
    isBoxFrame,
    MAX_PIN_FAILURES,
    type MasterMessage,
    parseClearFrame,
    parseCompanionMessage,
    parseMasterMessage,
    PROTOCOL_VERSION,
    type RefusedReason,
} from './messages'
import { pinToW, sameMac, spake2A, spake2B, type Spake2Party } from './spake2'

export interface Session {
    sid: string
    ke: string
}

export interface Link {
    send: (frame: unknown) => void
    close: () => void
}

export type Role = 'master' | 'companion'
export type Hello = Extract<CompanionMessage, { type: 'hello' }>
export type FailReason = RefusedReason | 'busy' | 'protocol'

/** What one side receives from the other once the channel is open. */
export type PeerEvent =
    | { kind: 'joined'; hello: Hello }
    | { kind: 'from_companion'; message: CompanionMessage }
    | { kind: 'from_master'; message: MasterMessage }
    | { kind: 'left' }

type Sent<R extends Role> = R extends 'master' ? MasterMessage : CompanionMessage

/**
 * The encrypted half of a connection, the same whichever side listens. A companion sends `hello` as soon as the
 * channel opens; a master accepts nothing before it.
 */
class Endpoint {
    private channel?: Channel
    private joined = false

    constructor(
        private readonly role: Role,
        private readonly link: Link,
        private readonly hello: Omit<Hello, 'type' | 'v'> | undefined,
        private readonly emit: (e: PeerEvent) => void,
    ) {}

    get isOpen() {
        return this.channel !== undefined
    }

    attach(channel: Channel) {
        this.channel = channel
    }

    open(channel: Channel) {
        this.channel = channel
        if (this.role === 'companion') {
            this.joined = true
            this.send({ type: 'hello', v: PROTOCOL_VERSION, ...this.hello! })
        }
    }

    send(message: MasterMessage | CompanionMessage) {
        if (this.channel && this.joined) this.link.send(this.channel.seal(message))
    }

    /** False when the frame is not acceptable and the connection must end. */
    receive(frame: unknown): boolean {
        if (!this.channel || !isBoxFrame(frame)) return false
        const raw = this.channel.open(frame)
        if (this.role === 'companion') {
            const message = parseMasterMessage(raw)
            if (!message) return false
            this.emit({ kind: 'from_master', message })
            return true
        }
        const message = parseCompanionMessage(raw)
        if (!message) return false
        if (!this.joined) {
            if (message.type !== 'hello' || message.v !== PROTOCOL_VERSION) return false
            this.joined = true
            this.emit({ kind: 'joined', hello: message })
            return true
        }
        if (message.type === 'ping') this.send({ type: 'pong' })
        else this.emit({ kind: 'from_companion', message })
        return true
    }

    shut() {
        this.channel = undefined
        if (this.joined) {
            this.joined = false
            this.emit({ kind: 'left' })
        }
    }
}

export interface ClientOptions<R extends Role> {
    role: R
    link: Link
    /** Required for a companion. */
    hello?: Omit<Hello, 'type' | 'v'>
    onEvent: (e: PeerEvent) => void
    onReady: (session: Session) => void
    onFailed: (reason: FailReason) => void
}

/** The connecting side. Feed it every frame the WebSocket receives. */
export class PairingClient<R extends Role> {
    private party?: Spake2Party
    private readonly nonce = randomBytes(16)
    private session?: Session
    private readonly endpoint: Endpoint
    private done = false

    constructor(private readonly o: ClientOptions<R>) {
        this.endpoint = new Endpoint(o.role, o.link, o.hello, o.onEvent)
    }

    pair(pin: string) {
        this.party = spake2A(pinToW(pin))
        this.o.link.send({ t: 'pake1', v: PROTOCOL_VERSION, pA: bytesToHex(this.party.message), n: bytesToHex(this.nonce) })
    }

    resume(session: Session) {
        this.session = session
        this.o.link.send({ t: 'resume', v: PROTOCOL_VERSION, sid: session.sid, n: bytesToHex(this.nonce) })
    }

    send(message: Sent<R>) {
        this.endpoint.send(message)
    }

    receive(frame: unknown) {
        if (this.done) return
        try {
            if (this.endpoint.isOpen) {
                if (!this.endpoint.receive(frame)) this.fail('protocol')
                return
            }
            const f = parseClearFrame(frame)
            if (!f) return this.fail('protocol')
            this.onClear(f)
        } catch {
            this.fail('protocol')
        }
    }

    /** Call when the socket closes. */
    closed() {
        this.finish()
    }

    close() {
        this.finish()
        this.o.link.close()
    }

    private onClear(f: ClearFrame) {
        if (f.t === 'busy') return this.fail('busy')
        if (f.t === 'refused') return this.fail(f.reason)
        if (f.t === 'pake2' && this.party) {
            const keys = this.party.finish(hexToBytes(f.pB))
            this.party = undefined
            if (!sameMac(keys.cB, hexToBytes(f.cB))) return this.fail('bad_pin')
            this.session = { sid: sessionId(keys.tt), ke: bytesToHex(keys.ke) }
            return this.open(f.n, { t: 'pake3', cA: bytesToHex(keys.cA) })
        }
        if (f.t === 'resume_ok' && this.session && !this.party) return this.open(f.n)
        this.fail('protocol')
    }

    // The channel is open before the last clear frame goes out, so a peer that answers at once is understood.
    private open(hostNonce: string, last?: ClearFrame) {
        const k = connectionKeys(hexToBytes(this.session!.ke), this.nonce, hexToBytes(hostNonce))
        const channel = Channel.client(k)
        this.endpoint.attach(channel)
        if (last) this.o.link.send(last)
        this.o.onReady(this.session!)
        this.endpoint.open(channel)
    }

    private fail(reason: FailReason) {
        if (this.done) return
        this.finish()
        this.o.onFailed(reason)
        this.o.link.close()
    }

    private finish() {
        this.done = true
        this.endpoint.shut()
    }
}

/**
 * The listening side's pairing state for one shift: the open PIN (single use, closed after three failures), the
 * session made by the last pairing, and the one connected device.
 */
export class HostPairing {
    private pin: string | null = null
    private failures = 0
    private session: Session | null = null
    private active: HostConnection | null = null

    constructor(
        private readonly role: Role,
        private readonly hello?: Omit<Hello, 'type' | 'v'>,
        private readonly onPinFailed?: (failures: number, locked: boolean) => void,
    ) {}

    open(pin: string) {
        this.pin = pin
        this.failures = 0
    }

    stopPairing() {
        this.pin = null
    }

    get isOpen() {
        return this.pin !== null
    }

    get current() {
        return this.active
    }

    forget() {
        this.pin = null
        this.session = null
        this.active?.close()
    }

    accept(link: Link, onEvent: (e: PeerEvent) => void): HostConnection | null {
        if (this.active) {
            link.send({ t: 'busy' })
            link.close()
            return null
        }
        const c = new HostConnection(this, link, new Endpoint(this.role, link, this.hello, onEvent))
        this.active = c
        return c
    }

    /** @internal */
    takePin() {
        return this.pin
    }

    /** @internal */
    failed() {
        this.failures++
        const locked = this.failures >= MAX_PIN_FAILURES
        if (locked) this.pin = null
        this.onPinFailed?.(this.failures, locked)
        return locked
    }

    /** @internal */
    paired(session: Session) {
        this.pin = null
        this.failures = 0
        this.session = session
    }

    /** @internal */
    knownSession(sid: string) {
        return this.session?.sid === sid ? this.session : null
    }

    /** @internal */
    released(c: HostConnection) {
        if (this.active === c) this.active = null
    }
}

export class HostConnection {
    private readonly nonce = randomBytes(16)
    private pending?: { cA: Uint8Array; session: Session; clientNonce: string }
    private done = false

    constructor(
        private readonly pairing: HostPairing,
        private readonly link: Link,
        private readonly endpoint: Endpoint,
    ) {}

    send(message: MasterMessage | CompanionMessage) {
        this.endpoint.send(message)
    }

    receive(frame: unknown) {
        if (this.done) return
        try {
            if (this.endpoint.isOpen) {
                if (!this.endpoint.receive(frame)) this.close()
                return
            }
            const f = parseClearFrame(frame)
            if (!f) return this.close()
            this.onClear(f)
        } catch {
            this.close()
        }
    }

    private onClear(f: ClearFrame) {
        if ((f.t === 'pake1' || f.t === 'resume') && f.v !== PROTOCOL_VERSION) return this.refuse('version')
        if (f.t === 'pake1' && !this.pending) {
            const pin = this.pairing.takePin()
            if (pin === null) return this.refuse('not_pairing')
            const party = spake2B(pinToW(pin))
            const keys = party.finish(hexToBytes(f.pA))
            this.pending = { cA: keys.cA, session: { sid: sessionId(keys.tt), ke: bytesToHex(keys.ke) }, clientNonce: f.n }
            return this.link.send({ t: 'pake2', pB: bytesToHex(party.message), n: bytesToHex(this.nonce), cB: bytesToHex(keys.cB) })
        }
        if (f.t === 'pake3' && this.pending) {
            const p = this.pending
            this.pending = undefined
            if (!sameMac(p.cA, hexToBytes(f.cA))) return this.refuse(this.pairing.failed() ? 'locked' : 'bad_pin')
            this.pairing.paired(p.session)
            return this.open(p.session, p.clientNonce)
        }
        if (f.t === 'resume' && !this.pending) {
            const session = this.pairing.knownSession(f.sid)
            if (!session) return this.refuse('unknown_session')
            return this.open(session, f.n, { t: 'resume_ok', n: bytesToHex(this.nonce) })
        }
        this.close()
    }

    private open(session: Session, clientNonce: string, last?: ClearFrame) {
        const channel = Channel.host(connectionKeys(hexToBytes(session.ke), hexToBytes(clientNonce), this.nonce))
        this.endpoint.attach(channel)
        if (last) this.link.send(last)
        this.endpoint.open(channel)
    }

    private refuse(reason: RefusedReason) {
        this.link.send({ t: 'refused', reason })
        this.close()
    }

    /** Call when the socket closes. A handshake abandoned after `pake2` counts as a wrong PIN. */
    closed() {
        if (this.pending) {
            this.pending = undefined
            this.pairing.failed()
        }
        this.finish()
    }

    close() {
        this.finish()
        this.link.close()
    }

    private finish() {
        if (this.done) return
        this.done = true
        this.endpoint.shut()
        this.pairing.released(this)
    }
}
